import { NextPageContext } from 'next'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'
import BasicHead from '../../../components/common/BasicHead'
import CsrOnlyStudentBrazePixel from '../../../components/common/CsrOnlyStudentBrazePixel'
import DeviceSplit from '../../../components/common/DeviceSplit'
import { GTM_EVENT, sendGTMEvent } from '../../../helpers/gtmHelper'
import { ISeoData } from '../../../model/Common/common.interface'
import { logVisitEvent } from '../../../modules/braze/logVisitRecord'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { RINGLE_DOMAIN } from '../../../modules/envVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import { LOCALE_KO } from '../../../modules/i18n/config'
import { s_head } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { getOgUrl, t } from '../../../modules/utils'

const EnglishTutorBodyV2Mobile = dynamic(() => import('../../../components/landing/body/mobile/EnglishTutorBodyV2'), {
  ssr: false,
})

const EnglishTutorBodyV2Desktop = dynamic(() => import('../../../components/landing/body/desktop/EnglishTutorBodyV2'), {
  ssr: false,
})

const Index = ({ ogData, seoData, isMobileServer }) => {
  const { locale } = useTranslation()
  const isMobile = useIsMobile(isMobileServer)
  const { currentUser } = useCurrentUser()
  const router = useAppRouter()

  useEffect(() => {
    if (!router) return

    sendGTMEvent(GTM_EVENT.VIEW_CONTENT, {
      page_location: 'Landing_Tutor',
    })
  }, [router.asPath])

  return (
    <>
      <BasicHead ogData={ogData} seoData={seoData} />
      <CsrOnlyStudentBrazePixel
        currentUser={currentUser}
        callback={(appBoy) =>
          logVisitEvent(appBoy, 'view_landing', {
            property_1: 'english-tutors',
            locale: locale,
          })
        }
      />
      <DeviceSplit
        isMobile={isMobile}
        mobile={<EnglishTutorBodyV2Mobile variation={null} />}
        desktop={<EnglishTutorBodyV2Desktop variation={null} />}
      />
    </>
  )
}

Index.getInitialProps = async (context: NextPageContext) => {
  const { lang = LOCALE_KO } = context.query

  const title =
    lang == LOCALE_KO ? '원어민화상영어 영어발음 |  링글 일대일 화상영어 튜터' : t(s_head.student.title, lang)
  const description =
    lang == LOCALE_KO
      ? '원어민화상영어 영어발음 No.1 일대일 화상영어 링글 스탠퍼드 MBA출신이 만든 1:1 화상 영어 명문대 원어민 튜터의 똑똑한 수업을 경험하세요!'
      : t(s_head.student.desc, lang)

  const ogData = {
    title,
    desc: description,
    ogUrl: getOgUrl({ lang, context }),
  }

  const seoData: ISeoData = {
    pageTitle: title,
    pageDescription: description,
    pageKeywords:
      lang === LOCALE_KO
        ? '원어민화상영어, 영어발음, 영어회화, 전화영어, 영어문장, 성인영어회화, 원어민화상영어, 영어회화책, 성인화상영어, 영어회화공부, 영어회화스터디, 비즈니스영어, 영어잘하는법, 기본영어회화, 영국영어, 직장인영어회화, 비지니스영어, 원어민영어회화, 전화영어회화, 비즈니스영어회화, 화상영어사이트, 영어회화학원, 성인영어회화학원'
        : null,
    pageCanonicalPath: `https://${RINGLE_DOMAIN}/${lang}/1on1/tutor`,
  }

  let userAgent: string

  if (context.req) {
    // if you are on the server and you get a 'req' property from your context
    userAgent = context.req.headers['user-agent'] // get the user-agent from the headers
  } else {
    userAgent = navigator.userAgent // if you are on the client you can access the navigator from the window object
  }

  const isMobileServer = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i))

  return {
    ogData,
    seoData,
    isMobileServer,
  }
}

export default Index
